import { safeParseDecimal } from "@/lib/api/common";
import { SuccessResult, ValidationError, getSuccessOrThrow } from "@/lib/responseResults";
import moment from "moment";
import { z } from "zod";

export type RangeType = "<" | "<=" | ">" | ">=" | "between";
export type ConstraintType = "hedged_percent";

export interface RecommendationRuleInput {
  rangeType: RangeType;
  constraintType: ConstraintType;
  rangeConstraintValue: string;
  message: string;
}

interface NumericRange {
  min: number | null; // null means -infinity
  max: number | null; // null means +infinity
  minInclusive: boolean; // whether min is inclusive
  maxInclusive: boolean; // whether max is inclusive
  rangeType: RangeType;
  originalValue: string;
}

const VALID_RANGE_TYPES: RangeType[] = ["<", "<=", ">", ">=", "between"];
const VALID_CONSTRAINT_TYPES: ConstraintType[] = ["hedged_percent"];

/**
 * Zod schema for YYYY-MM-DD date format
 * Validates format and ensures it's a valid date using moment
 */
const dateStringSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
  .refine(
    (dateStr: string) => {
      const parsed = moment(dateStr, "YYYY-MM-DD", true);
      return parsed.isValid() && parsed.format("YYYY-MM-DD") === dateStr;
    },
    {
      message: "Date must be a valid date",
    }
  );

/**
 * Zod schema for RecommendationRuleInput
 */
export const RecommendationRuleInputSchema = z.object({
  rangeType: z.enum(["<", "<=", ">", ">=", "between"]),
  constraintType: z.enum(["hedged_percent"]),
  rangeConstraintValue: z.string().min(1, "Range constraint value cannot be empty"),
  message: z.string().min(1, "Message cannot be empty").trim(),
});

/**
 * Zod schema for ListRecommendationsOptions
 */
export const ListRecommendationsOptionsSchema = z.object({
  date: dateStringSchema.optional(),
  startDate: dateStringSchema.optional(),
  endDate: dateStringSchema.optional(),
  cropYear: z.number().int().optional(),
  commodityType: z.string().optional(),
}).refine(
  (data: { date?: string; startDate?: string; endDate?: string }) => {
    // If date is provided, startDate and endDate should not be provided
    if (data.date && (data.startDate || data.endDate)) {
      return false;
    }
    return true;
  },
  {
    message: "Cannot provide both 'date' and 'startDate'/'endDate'",
  }
);

/**
 * Zod schema for UpsertRecommendationGroupData
 */
export const UpsertRecommendationGroupDataSchema = z.object({
  recommendationDate: dateStringSchema,
  cropYear: z.number().int(),
  commodityType: z.string().min(1, "Commodity type cannot be empty"),
  rules: z.array(RecommendationRuleInputSchema).min(1, "At least one rule is required"),
});

/**
 * Helper function to convert Zod errors to ValidationError
 */
function validateWithZod<T>(schema: z.ZodSchema<T>, data: unknown): T {
  try {
    return schema.parse(data);
  } catch (error: unknown) {
    if (error instanceof z.ZodError) {
      const errorMessages = error.issues.map((err: z.ZodIssue) => {
        const path = err.path.length > 0 ? `${err.path.join(".")}: ` : "";
        return `${path}${err.message}`;
      });
      throw new ValidationError(errorMessages.join("; "), {});
    }
    throw error;
  }
}

/**
 * Validates a RecommendationRuleInput using Zod schema
 */
export function validateRecommendationRuleInput(data: unknown): RecommendationRuleInput {
  return validateWithZod(RecommendationRuleInputSchema, data);
}

/**
 * Type inferred from ListRecommendationsOptionsSchema
 */
export type ListRecommendationsOptions = z.infer<typeof ListRecommendationsOptionsSchema>;

/**
 * Type inferred from UpsertRecommendationGroupDataSchema
 */
export type UpsertRecommendationGroupData = z.infer<typeof UpsertRecommendationGroupDataSchema>;

/**
 * Validates ListRecommendationsOptions using Zod schema
 */
export function validateListRecommendationsOptions(data: unknown): ListRecommendationsOptions {
  return validateWithZod(ListRecommendationsOptionsSchema, data);
}

/**
 * Validates UpsertRecommendationGroupData using Zod schema
 */
export function validateUpsertRecommendationGroupData(data: unknown): UpsertRecommendationGroupData {
  return validateWithZod(UpsertRecommendationGroupDataSchema, data);
}

/**
 * Validates that a range type is valid
 */
export function validateRangeType(rangeType: string): rangeType is RangeType {
  return VALID_RANGE_TYPES.includes(rangeType as RangeType);
}

/**
 * Validates that a constraint type is valid
 */
export function validateConstraintType(constraintType: string): constraintType is ConstraintType {
  return VALID_CONSTRAINT_TYPES.includes(constraintType as ConstraintType);
}

/**
 * Parses a range constraint value into a numeric range
 */
function parseRangeConstraintValue(
  rangeType: RangeType,
  rangeConstraintValue: string
): NumericRange {
  const trimmed = rangeConstraintValue.trim();

  if (rangeType === "between") {
    const parts = trimmed.split("-");
    if (parts.length !== 2) {
      throw new ValidationError(
        `Invalid 'between' range format. Expected 'min-max', got: ${trimmed}`,
        {}
      );
    }

    const { min, max } = getSuccessOrThrow((() => {
      try {
        const min = safeParseDecimal({ throwOnInvalid: true })(parts?.[0]?.trim())!;
        const max = safeParseDecimal({ throwOnInvalid: true })(parts?.[1]?.trim())!;

        if (min.gte(max)) {
          return new ValidationError(
            `Invalid 'between' range: min (${min}) must be less than max (${max})`,
            {}
          );
        }

        return new SuccessResult("", { min: min.toNumber(), max: max.toNumber() });
      } catch(error) {
        return new ValidationError(
          `Invalid numeric values in 'between' range: ${trimmed}`,
          {}
        );
      }
    })());

    return { min, max, minInclusive: true, maxInclusive: true, rangeType, originalValue: trimmed };
  } else {
    const value = parseFloat(trimmed);
    if (isNaN(value)) {
      throw new ValidationError(
        `Invalid numeric value for range type '${rangeType}': ${trimmed}`,
        {}
      );
    }

    switch (rangeType) {
      case "<":
        return { min: null, max: value, minInclusive: true, maxInclusive: false, rangeType, originalValue: trimmed };
      case "<=":
        return { min: null, max: value, minInclusive: true, maxInclusive: true, rangeType, originalValue: trimmed };
      case ">":
        return { min: value, max: null, minInclusive: false, maxInclusive: true, rangeType, originalValue: trimmed };
      case ">=":
        return { min: value, max: null, minInclusive: true, maxInclusive: true, rangeType, originalValue: trimmed };
      default:
        throw new ValidationError(`Unsupported range type: ${rangeType}`, {});
    }
  }
}

/**
 * Checks if two numeric ranges overlap
 * Accounts for exclusive boundaries (e.g., < 10 does not overlap with >= 10)
 * Two ranges overlap if they share at least one point
 */
function rangesOverlap(range1: NumericRange, range2: NumericRange): boolean {
  const min1 = range1.min ?? -Infinity;
  const max1 = range1.max ?? Infinity;
  const min2 = range2.min ?? -Infinity;
  const max2 = range2.max ?? Infinity;

  // Check if range1 ends before range2 starts (no overlap)
  if (max1 < min2) return false;
  if (max1 === min2) {
    // They touch at the boundary - check if both sides are inclusive
    // If max1 is exclusive OR min2 is exclusive, they don't overlap
    if (!range1.maxInclusive || !range2.minInclusive) return false;
  }

  // Check if range2 ends before range1 starts (no overlap)
  if (max2 < min1) return false;
  if (max2 === min1) {
    // They touch at the boundary - check if both sides are inclusive
    // If max2 is exclusive OR min1 is exclusive, they don't overlap
    if (!range2.maxInclusive || !range1.minInclusive) return false;
  }

  // If we get here, the ranges overlap (they share at least one point)
  return true;
}

/**
 * Validates a list of recommendation rules to ensure:
 * 1. All rules have valid range types and constraint types
 * 2. All rules have the same constraint type
 * 3. No overlapping ranges exist
 */
export function validateRecommendationRules(
  rules: RecommendationRuleInput[]
): void {
  if (rules.length === 0) {
    throw new ValidationError("At least one rule is required", {});
  }

  // Validate all rules have valid types
  for (const rule of rules) {
    if (!validateRangeType(rule.rangeType)) {
      throw new ValidationError(
        `Invalid range type: ${rule.rangeType}. Valid types are: ${VALID_RANGE_TYPES.join(", ")}`,
        {}
      );
    }

    if (!validateConstraintType(rule.constraintType)) {
      throw new ValidationError(
        `Invalid constraint type: ${rule.constraintType}. Valid types are: ${VALID_CONSTRAINT_TYPES.join(", ")}`,
        {}
      );
    }

    if (!rule.message || rule.message.trim().length === 0) {
      throw new ValidationError("Rule message cannot be empty", {});
    }
  }

  // Ensure all rules have the same constraint type
  const firstConstraintType = rules[0].constraintType;
  for (let i = 1; i < rules.length; i++) {
    if (rules[i].constraintType !== firstConstraintType) {
      throw new ValidationError(
        `All rules must have the same constraint type. Found: ${firstConstraintType} and ${rules[i].constraintType}`,
        {}
      );
    }
  }

  // Parse all ranges and check for overlaps
  const parsedRanges: NumericRange[] = [];
  for (const rule of rules) {
    try {
      const parsed = parseRangeConstraintValue(rule.rangeType, rule.rangeConstraintValue);
      parsedRanges.push(parsed);
    } catch (error) {
      // Re-throw validation errors as-is
      if (error instanceof ValidationError) {
        throw error;
      }
      throw new ValidationError(
        `Failed to parse range constraint value: ${rule.rangeConstraintValue}`,
        {}
      );
    }
  }

  // Check for overlaps
  for (let i = 0; i < parsedRanges.length; i++) {
    for (let j = i + 1; j < parsedRanges.length; j++) {
      if (rangesOverlap(parsedRanges[i], parsedRanges[j])) {
        throw new ValidationError(
          `Overlapping ranges detected: "${parsedRanges[i].originalValue}" (${parsedRanges[i].rangeType}) overlaps with "${parsedRanges[j].originalValue}" (${parsedRanges[j].rangeType})`,
          {}
        );
      }
    }
  }
}
